import { formatMonth } from "../lib/dates";
import { money } from "../lib/format";
import type { PriorityOutcome } from ".";

/**
 * Words for a priority's outcome in one plan: always how much of it is funded ("78% funded"),
 * never a date (v40: the household sets the date, so the engine's answer is how much of the goal
 * gets paid for by then).
 */
export function outcomeLabel(o: PriorityOutcome, which: "abi" | "naive"): string {
  const f = o[which];
  if (!f) return "Not in this plan";
  return `${Math.round(Math.max(0, Math.min(1, f.completion)) * 100)}% funded`;
}

/**
 * The difference between the two plans (v40/v44 `priWin`), derived from the two fractions so the
 * words can't drift from the numbers: "Fully paid for" when Abi's plan takes it to 100%, else "33%
 * more of it paid for" (a share of the goal itself, not points), "… less …", or "No change".
 */
export function winLabel(o: PriorityOutcome): string {
  const a = Math.min(1, Math.max(0, o.abi?.completion ?? 0));
  const n = Math.min(1, Math.max(0, o.naive?.completion ?? 0));
  const d = Math.round((a - n) * 100);
  if (d === 0) return "No change";
  if (Math.round(a * 100) >= 100 && d > 0) return "Fully paid for";
  return `${Math.abs(d)}% ${d > 0 ? "more" : "less"} of it paid for`;
}

/** A home-buying goal's terms: "A $650,000 home, $130,000 down, in June 2029". */
export function homeLine(home: NonNullable<PriorityOutcome["home"]>): string {
  const down = home.downPayment ? `, ${money(home.downPayment)} down` : "";
  const when = formatMonth(home.due);
  return `A ${money(home.price)} home${down}${when ? `, in ${when}` : ""}`;
}
